import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { TabBar } from "@/components/toggles";
import { ArrowLeft, Package, Eye, Edit3, Trash2, X, AlertTriangle, ShoppingBag, Sparkles } from "lucide-react";
import { InfoCard } from "@/components/toggles";

export default function MyListingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("marketplace"); // "marketplace" | "stx"

  const STATUS_LABEL = {
    PUBLISHED: { text: t("my_listings.status_active"), cls: "text-green-600 border-green-200 bg-green-50" },
    DRAFT: { text: t("my_listings.status_draft"), cls: "text-amber-600 border-amber-200 bg-amber-50" },
    ARCHIVED: { text: t("my_listings.status_archived"), cls: "text-amber-500 border-amber-200 bg-amber-50" },
    UNAVAILABLE: { text: t("my_listings.status_unavailable"), cls: "text-amber-500 border-amber-200 bg-amber-50" },
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/seller/listings");
      const all = data.listings || [];
      setListings(all.filter(l => l.listing_status !== "DELETED"));
    } catch { setListings([]); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    load();
  }, [user, navigate]);

  const doDelete = async () => {
    if (!confirmDel) return;
    setDeletingId(confirmDel.id);
    setError(null);
    try {
      await api.delete(`/listings/${confirmDel.id}`);
      setConfirmDel(null);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.message || "Fehler beim Löschen"));
    } finally {
      setDeletingId(null);
    }
  };

  // Sub-Batch BUGFIX-C: classify listings into MP/STX
  const { mpListings, stxListings } = useMemo(() => {
    return {
      mpListings: listings.filter((l) => l.area_scope !== "STILLTOUCHEDX"),
      stxListings: listings.filter((l) => l.area_scope === "STILLTOUCHEDX"),
    };
  }, [listings]);

  const visibleListings = tab === "stx" ? stxListings : mpListings;

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;

  return (
    <div data-testid="my-listings-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button data-testid="listings-back" onClick={() => navigate(-1)} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold text-[#0A0A0A]">{t("my_listings.title")}</h1>
        <span className="ml-auto text-xs text-[#999]">{t("my_listings.items_count", { count: listings.length })}</span>
      </div>

      {/* Tabs MP / STX */}
      <TabBar
        tabs={[
          {
            key: "marketplace",
            label: t("my_listings.tab_marketplace"),
            icon: <ShoppingBag size={14} />,
            suffix: `(${mpListings.length})`,
            testId: "my-listings-tab-marketplace",
          },
          {
            key: "stx",
            label: t("my_listings.tab_stx"),
            icon: <Sparkles size={14} />,
            suffix: `(${stxListings.length})`,
            testId: "my-listings-tab-stx",
          },
        ]}
        activeKey={tab}
        onChange={setTab}
        sticky
      />

      {error && (
        <InfoCard variant="danger" className="mx-4 mt-3">
          <AlertTriangle size={14} className="text-red-600 mt-0.5 shrink-0" />
          <p className="text-xs text-red-700 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-600"><X size={14} /></button>
        </InfoCard>
      )}

      {visibleListings.length === 0 ? (
        <div className="px-4 py-16 text-center">
          <Package size={32} className="mx-auto text-[#E5E5E5] mb-3" />
          <p className="text-sm text-[#999]">
            {tab === "stx" ? t("my_listings.empty_stx") : t("my_listings.empty_mp")}
          </p>
          <button data-testid="create-listing-btn" onClick={() => navigate("/listing/new")}
            className="mt-4 rounded-full h-10 px-6 bg-[#0A0A0A] text-white text-sm font-medium">{t("my_listings.create_item")}</button>
        </div>
      ) : (
        <div className="divide-y divide-[#E5E5E5]">
          {visibleListings.map((l) => {
            // BUGFIX-C: only TRULY sold listings (sold_at set) are locked.
            // Out-of-stock (quantity=0, no sold_at) remains fully editable.
            const isTrulySold = (l.listing_status === "SOLD_OUT" || l.listing_status === "SOLD") && !!l.sold_at;
            const isOutOfStock = (l.listing_status === "SOLD_OUT" || l.listing_status === "SOLD") && !l.sold_at;
            const statusInfo = STATUS_LABEL[l.listing_status];

            let badgeText;
            let badgeCls;
            if (isTrulySold) {
              badgeText = t("my_listings.status_sold");
              badgeCls = "text-[#999] border-[#E5E5E5] bg-[#F5F5F5]";
            } else if (isOutOfStock) {
              badgeText = t("my_listings.status_out_of_stock");
              badgeCls = "text-amber-700 border-amber-300 bg-amber-50";
            } else if (statusInfo) {
              badgeText = statusInfo.text;
              badgeCls = statusInfo.cls;
            } else {
              badgeText = l.listing_status;
              badgeCls = "text-[#666] border-[#E5E5E5] bg-[#FAFAFA]";
            }

            return (
              <div key={l.listing_id} data-testid={`listing-${l.listing_id}`}
                className={`flex gap-3 px-4 py-3 transition-colors ${isTrulySold ? "bg-[#FAFAFA]" : "hover:bg-[#FAFAFA]"}`}>
                <div className="w-16 h-20 bg-[#F5F5F5] shrink-0 overflow-hidden cursor-pointer relative"
                  onClick={() => navigate(l.area_scope === "STILLTOUCHEDX" ? `/stilltouchedx/${l.listing_id}` : `/marketplace/${l.listing_id}`)}>
                  {l.media_urls?.[0] ? <img src={l.media_urls[0]} alt="" className="w-full h-full object-cover" /> : (
                    <div className="w-full h-full flex items-center justify-center"><Package size={16} className="text-[#CCC]" /></div>
                  )}
                  {/* Sub-Batch IMG-2: Indikator wenn mehrere Bilder vorhanden */}
                  {(l.media_urls?.length || 0) > 1 && (
                    <span data-testid={`mylisting-img-count-${l.listing_id}`}
                      className="absolute top-0.5 left-0.5 px-1 py-0.5 bg-black/60 text-white text-[9px] font-medium">
                      +{l.media_urls.length - 1}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0A0A0A] truncate">{l.title}</p>
                  <p className="text-xs text-[#999] mt-0.5">
                    {l.public_item_no} · {l.area_scope === "STILLTOUCHEDX" ? "STX" : "MP"}
                    {typeof l.quantity === "number" && <> · <span className={l.quantity === 0 ? "text-amber-700 font-medium" : ""}>{t("my_listings.units", { count: l.quantity })}</span></>}
                  </p>
                  <p className="text-sm font-bold text-[#0A0A0A] mt-1">{l.price?.toFixed(2)} {l.currency}</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-medium border ${badgeCls}`}>{badgeText}</span>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    data-testid={`listing-view-${l.listing_id}`}
                    onClick={() => navigate(l.area_scope === "STILLTOUCHEDX" ? `/stilltouchedx/${l.listing_id}` : `/marketplace/${l.listing_id}`)}
                    className="h-7 px-2 border border-[#E5E5E5] text-[#666] text-[10px] flex items-center gap-1 hover:bg-[#FAFAFA]"
                    title={t("my_listings.tt_view")}
                  >
                    <Eye size={11} />
                  </button>
                  <button
                    data-testid={`listing-edit-${l.listing_id}`}
                    onClick={() => navigate(`/listing/edit/${l.listing_id}`)}
                    disabled={isTrulySold}
                    className={`h-7 px-2 text-[10px] flex items-center gap-1 ${
                      isTrulySold
                        ? "border border-[#F0F0F0] text-[#CCC] cursor-not-allowed"
                        : "border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                    }`}
                    title={isTrulySold ? t("my_listings.tt_edit_sold") : isOutOfStock ? t("my_listings.tt_edit_out_of_stock") : t("my_listings.tt_edit")}
                  >
                    <Edit3 size={11} />
                  </button>
                  {l.area_scope === "STILLTOUCHEDX" && (
                    <button
                      data-testid={`listing-preview-config-${l.listing_id}`}
                      onClick={() => navigate(`/studio/listings/${l.listing_id}/preview-config`)}
                      disabled={isTrulySold}
                      className={`h-7 px-2 text-[10px] flex items-center gap-1 ${
                        isTrulySold
                          ? "border border-[#F0F0F0] text-[#CCC] cursor-not-allowed"
                          : (l.preview_config?.try_on_enabled
                              ? "border border-emerald-500 text-emerald-700 bg-emerald-50"
                              : "border border-[#E5E5E5] text-[#666] hover:border-[#0A0A0A] hover:text-[#0A0A0A]")
                      }`}
                      title={l.preview_config?.try_on_enabled ? "Try-On aktiv – konfigurieren" : "Try-On konfigurieren"}
                    >
                      <Sparkles size={11} />
                    </button>
                  )}
                  <button
                    data-testid={`listing-delete-${l.listing_id}`}
                    onClick={() => setConfirmDel({ id: l.listing_id, title: l.title })}
                    disabled={isTrulySold || deletingId === l.listing_id}
                    className={`h-7 px-2 text-[10px] flex items-center gap-1 ${
                      isTrulySold
                        ? "border border-[#F0F0F0] text-[#CCC] cursor-not-allowed"
                        : "border border-red-300 text-red-700 hover:bg-red-50"
                    }`}
                    title={isTrulySold ? t("my_listings.tt_delete_sold") : t("my_listings.tt_delete")}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setConfirmDel(null)}>
          <div className="w-full max-w-sm bg-white border border-[#E5E5E5] shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
              <h3 className="text-sm font-bold text-red-700">{t("my_listings.delete_confirm_title")}</h3>
              <button onClick={() => setConfirmDel(null)}><X size={16} /></button>
            </div>
            <div className="px-4 py-3">
              <p className="text-sm text-[#0A0A0A]">{t("my_listings.delete_confirm_body", { title: confirmDel.title })}</p>
              <p className="text-xs text-[#666] mt-2">{t("my_listings.delete_undo_hint")}</p>
              <div className="flex gap-2 mt-4">
                <button
                  data-testid="delete-cancel"
                  onClick={() => setConfirmDel(null)}
                  className="flex-1 h-10 border border-[#E5E5E5] text-[#0A0A0A] text-sm font-medium"
                >
                  {t("my_listings.cancel")}
                </button>
                <button
                  data-testid="delete-confirm"
                  onClick={doDelete}
                  disabled={!!deletingId}
                  className="flex-1 h-10 bg-red-600 text-white text-sm font-medium hover:bg-red-500 disabled:opacity-50"
                >
                  {deletingId ? t("my_listings.deleting") : t("my_listings.delete")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
